import * as Headless from "@headlessui/react";
import { GlobeAltIcon } from "@heroicons/react/24/outline";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import clsx from "clsx";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";

type LanguageDropdownProps<T> = {
    className?: string;
    placeholder?: React.ReactNode;
    autoFocus?: boolean;
    "aria-label"?: string;
    children?: React.ReactNode;
} & Omit<Headless.ListboxProps<typeof Fragment, T>, "as" | "multiple">;

const LanguageDropdown = <T,>({
    className,
    autoFocus,
    "aria-label": ariaLabel,
    children: options,
    ...props
}: LanguageDropdownProps<T>) => {
    const { t } = useTranslation(["header"]);
    const selectedValue = props.value as { id: string; name: string };

    return (
        <Headless.Listbox {...props} multiple={false}>
            <Headless.ListboxButton
                autoFocus={autoFocus}
                data-slot="control"
                aria-label={ariaLabel}
                className={clsx([
                    className,
                    "group relative grid w-full gap-2 items-center mb-1 cursor-pointer",
                    "before:absolute before:inset-px before:rounded-[calc(var(--radius-lg)-1px)] before:bg-white before:shadow-sm",
                    "before:hidden",
                    "focus:outline-hidden",
                    "after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-transparent after:ring-inset data-focus:after:ring-2 data-focus:after:ring-blue-500",
                    "data-disabled:opacity-50 data-disabled:before:bg-zinc-950/5 data-disabled:before:shadow-none",
                ])}
            >
                <GlobeAltIcon className="col-start-1 size-6 stroke-1" />
                <span className="col-start-2 flex flex-col text-left line-h">
                    <span className="text-xs/[6px] opacity-50 font-medium">{t("language", { ns: "header" })}</span>{" "}
                    <span className="text-base/tight font-medium">{selectedValue?.name}</span>
                </span>

                <span className="col-start-3 pointer-events-none inset-y-0 flex items-center">
                    <ChevronDownIcon className="size-5 stroke-black opacity-50 group-data-disabled:stroke-zinc-600 sm:size-4  forced-colors:stroke-[CanvasText]" />
                </span>
            </Headless.ListboxButton>
            <Headless.ListboxOptions
                transition
                anchor="top end"
                modal={false}
                className={clsx(
                    "mt-2 z-50 p-4",
                    "isolate w-max scroll-py-1 rounded-xl p-1 select-none",
                    "outline outline-transparent focus:outline-hidden",
                    "overflow-y-scroll overscroll-contain",
                    "bg-white backdrop-blur-xl",
                    "ring-1 shadow-lg ring-[#E6E8EC]",
                    "transition-opacity duration-100 ease-in data-closed:data-leave:opacity-0 data-transition:pointer-events-none",
                    "grid grid-cols-1"
                )}
            >
                {options}
            </Headless.ListboxOptions>
        </Headless.Listbox>
    );
};

export default LanguageDropdown;
